import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ServicePathProviderService } from '@blk/cwp-ng-framework';
import { ConfigService } from '@ddt-web/config-layer';
import { PortfolioTaggingService } from '@ddt-web/data-service';
import { API_PARAM_TYPE, ApiParams, PortfolioWithWdsTagging, UserDataApi, WdsTaggingAttributes, WdsTaggingConfig, WebUserDataConstants } from '@ddt-web/domain';
import { select, Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

import { getSavedModelPortfolioIdMappings, getSavedModelPortfolioIds, SavedModelPortfoliosState } from '..';
import { SavedAndSubscribedFactory } from '../factories/saved-and-subscribed-factory';


@Injectable()
export class UserDataService implements UserDataApi {
	private savedModelPortfolioIdMappings$: Observable<Map<string, string>>;
	private savedModelPortfolioIds$: Observable<string[]>;

	constructor(
		private store: Store<SavedModelPortfoliosState>,
		private http: HttpClient,
		private servicePathProvider: ServicePathProviderService,
		private savedAndSubscribedFactory: SavedAndSubscribedFactory,
		private configService: ConfigService, // temporary: needed to bootstrap MSP
		private portfolioTaggingService: PortfolioTaggingService
	) {
		this.init();
	}

	private getAllJson$: Subject<any> = new Subject<any>();

	private init(): void {
		this.savedModelPortfolioIdMappings$ = this.store.pipe(select(getSavedModelPortfolioIdMappings));
		this.savedModelPortfolioIds$ = this.store.pipe(select(getSavedModelPortfolioIds));
	}

	public getSavedModelPortfolioIdMappings(): Observable<Map<string, string>> {
		return this.savedModelPortfolioIdMappings$;
	}

	public getSavedModelPortfolioIds(): Observable<string[]> {
		return this.savedModelPortfolioIds$;
	}

	public getAllJson(): Observable<any> {
		return this.getAllJson$.asObservable();
	}

	private fetchUserData(rcName: string, apiParams: ApiParams[] = []): Observable<any> {
		const url = this.servicePathProvider.getServicePath('api-gateway') + WebUserDataConstants.USER_DATA_URL.GET_ALL;
		const requestParam = { rc: rcName || '' };
		if (apiParams.length) {
			apiParams.forEach(param => {
				if (param.type === API_PARAM_TYPE.IDENTIFIER.PARAM) { requestParam[param.paramId] = param.value; }
			});
		}
		return this.http
			.get(url, {
				params: requestParam
			})
			.pipe(
				map(response => {
					const deltaUniqueIds: string[] = this.savedAndSubscribedFactory.getDeltaIds(response);
					const savedMaster: any = this.savedAndSubscribedFactory.getSavedMaster(response);
					const subscribedMaster: any = this.savedAndSubscribedFactory.getSubscribedMaster(response);

					this.updateMasterListPreference(savedMaster, subscribedMaster, deltaUniqueIds);

					return response;
				}),
				map(response => {
					this.getAllJson$.next(response);
					return response;
				})
			);
	}

	public loadUserDataJson(rcName: string, params: ApiParams[] = []): Observable<any> {
		return this.fetchUserData(rcName, params);
	}

	public updateMasterListPreference(savedMaster: any, subscribedMaster: any, deltaIds: string[]): void {
		if (deltaIds.length) {
			// eslint-disable-next-line @nrwl/nx/workspace/killSubscriptionRule
			this.updatePrefs(savedMaster, subscribedMaster).subscribe(
				response => {
					// eslint-disable-next-line @nrwl/nx/workspace/killSubscriptionRule
					this.removeUserDataObjectsFromAPI(deltaIds).subscribe();
				},
				error => {
					console.log(`Error occurred updating master list prefs: ${error}`);
				}
			);
		}
	}

	private updatePrefs(savedMaster: any, subscribedMaster: any): Observable<any> {
		const url = this.servicePathProvider.getServicePath('api-gateway') + WebUserDataConstants.USER_DATA_URL.EDIT;
		return this.http.post(url, {
			allowCreateIfMissing: 'true',
			rcs: 'bfa-new-data',
			datas: '[' + this.createEditJsonForAPI(savedMaster) + ',' + this.createEditJsonForAPI(subscribedMaster) + ']'
		});
	}

	private createEditJsonForAPI(masterList: {}): string {
		const dataObject = {
			preferenceKey: masterList['preferenceKey'],
			type: 'PORTFOLIO_LIKE',
			children: []
		};

		masterList['children'].forEach(child => {
			const childObject = {
				preferenceKey: child['preferenceKey'],
				identifier: child['identifier'],
				identifierType: 'PORTFOLIO_ID',
				type: 'PORTFOLIO_POSITION_LIKE'
			};

			dataObject['children'].push(childObject);
		});

		this.portfolioTaggingService.setAnalyticsTags(
			dataObject,
			{
				isNewRecord: true,
				overrides: {
					toolOrigin: WebUserDataConstants.APPLICATIONS.BFA_MODELS,
					origin: WebUserDataConstants.APPLICATIONS.BFA_MODELS
				} as WdsTaggingAttributes

			} as WdsTaggingConfig
		);

		return JSON.stringify(dataObject);
	}

	private removeUserDataObjectsFromAPI(uniqueIds: Array<string>): Observable<any> {
		const url = this.servicePathProvider.getServicePath('api-gateway') + WebUserDataConstants.USER_DATA_URL.REMOVE;
		return this.http.post(url, {
			uniqueIds: uniqueIds.toString()
		});
	}

	public subscribeBlkModelPortfolioToAPI(modelPortfolioId: string, origin: string): Observable<any> {
		return this.saveBlkModelPortfolioToAPI(modelPortfolioId, origin);
	}

	public unsubscribeBlkModelPortfolioToAPI(uniqueId: string): Observable<any> {
		return this.removeUserDataObjectsFromAPI([uniqueId]);
	}

	public saveBlkModelPortfolioToAPI(modelPortfolioId: string, origin: string): Observable<any> {
		const dataObject = {
			preferenceKey: 'user.BlkSavedModels.masterList',
			type: 'PORTFOLIO_LIKE',
			children: [
				{
					preferenceKey: 'user.BlkSavedModels.masterList.portfolio.' + modelPortfolioId,
					identifier: modelPortfolioId,
					identifierType: 'PORTFOLIO_ID',
					type: 'PORTFOLIO_POSITION_LIKE'
				}
			]
		};

		this.addTaggingToWdsSavedObjects(dataObject);

		const url = this.servicePathProvider.getServicePath('api-gateway') + WebUserDataConstants.USER_DATA_URL.ADD;
		return this.http.post(url, {
			data: JSON.stringify(dataObject),
			rcs: 'bfa-new-data'
		});
	}

	public saveAndSubscribeBlkModelPortfolioToAPI(modelPortfolioId: string, origin: string): Observable<any> {
		const dataObject = [
			{
				preferenceKey: 'user.BlkSavedModels.masterList',
				type: 'PORTFOLIO_LIKE',
				children: [
					{
						preferenceKey: 'user.BlkSavedModels.masterList.portfolio.' + modelPortfolioId,
						identifier: modelPortfolioId,
						identifierType: 'PORTFOLIO_ID',
						type: 'PORTFOLIO_POSITION_LIKE'
					}
				]
			},
			{
				preferenceKey: 'user.BlkSubscribedModels.masterList',
				type: 'PORTFOLIO_LIKE',
				children: [
					{
						preferenceKey: 'user.BlkSubscribedModels.masterList.portfolio.' + modelPortfolioId,
						identifier: modelPortfolioId,
						identifierType: 'PORTFOLIO_ID',
						type: 'PORTFOLIO_POSITION_LIKE'
					}
				]
			}
		];

		this.addTaggingToWdsSavedObjects(dataObject);

		const url = this.servicePathProvider.getServicePath('api-gateway') + WebUserDataConstants.USER_DATA_URL.ADD;
		return this.http.post(url, {
			datas: JSON.stringify(dataObject),
			rcs: 'bfa-new-data'
		});
	}

	public unsaveAndUnsubscribeBlkModelPortfolioToAPI(uniqueIds: string[]): Observable<any> {
		return this.removeUserDataObjectsFromAPI(uniqueIds);
	}

	public unsaveBlkModelPortfolioToAPI(uniqueId: string): Observable<any> {
		return this.removeUserDataObjectsFromAPI([uniqueId]);
	}

	public deleteUserSavedPortfolio(uniqueIds: string[]): Observable<any> {
		return this.removeUserDataObjectsFromAPI(uniqueIds);
	}

	private addTaggingToWdsSavedObjects(dataObject: PortfolioWithWdsTagging): void {
		this.portfolioTaggingService.setAnalyticsTags(
			dataObject,
			{
				isNewRecord: true,
				overrides: {
					toolOrigin: WebUserDataConstants.APPLICATIONS.BFA_MODELS,
					origin: origin,
					applications: [WebUserDataConstants.APPLICATIONS.BFA, WebUserDataConstants.APPLICATIONS.BFA_MODELS]
				} as WdsTaggingAttributes

			} as WdsTaggingConfig
		);
	}
}
